import { type FC, useRef, useEffect, useState } from 'react';
import { Field, type FieldProps } from 'formik';
import { TrixEditor } from 'react-trix';
import {
  UiFormControl,
  UiFormLabel,
  UiFormErrorMessage,
  UiFormHelperText,
  UiHStack,
  UiText,
  UiStack
} from '@/lib/ui';
import FieldContainer, { type FieldContainerProps } from './FieldContainer';

import 'trix/dist/trix';
import 'trix/dist/trix.css';

export interface RichTextAreaFieldProps extends Omit<FieldContainerProps, 'children'> {
  label: string
  name: string
  helperText?: string
  isRequired?: boolean
  placeholder?: string
}

const RichTextAreaField: FC<RichTextAreaFieldProps> = ({
  label,
  name,
  helperText = undefined,
  isRequired = true,
  layout = 'inline',
  placeholder = ''
}) => {
  const editorRef = useRef<any>(null);
  const [hasLoadedInitialValue, setHasLoadedInitialValue] = useState(false);
  
  // Prevent file attachments
  useEffect(() => {
    const preventFileAttachment = (e: Event) => {
      e.preventDefault();
      return false;
    };
    
    document.addEventListener('trix-file-accept', preventFileAttachment);
    
    return () => {
      document.removeEventListener('trix-file-accept', preventFileAttachment);
    };
  }, []);

  return (
    <Field name={name}>
      {({ field, form }: FieldProps) => {
        const handleChange = (html: string) => {
          form.setFieldValue(name, html);
        };

        // Load initial value when editor becomes available
        useEffect(() => {
          const timer = setTimeout(() => {
            if (editorRef.current?.editor && field.value && !hasLoadedInitialValue) {
              try {
                editorRef.current.editor.loadHTML(field.value);
                setHasLoadedInitialValue(true);
              } catch (error) {
                console.warn('Failed to load HTML into Trix editor:', error);
              }
            }
          }, 100); // Small delay to ensure editor is fully initialized
          
          return () => clearTimeout(timer);
        }, [field.value, hasLoadedInitialValue]);

        return (
          <UiFormControl isInvalid={!!form.errors[name]} flexGrow={1}>
            <FieldContainer layout={layout}>
              {isRequired ? (
                <UiHStack alignItems={'flex-start'} spacing={0}>
                  <UiFormLabel>{label}</UiFormLabel>
                  <UiText color={'gray.600'} variant='title'>*</UiText>
                </UiHStack>
              ) : (
                <UiFormLabel>{label}</UiFormLabel>
              )}
              <UiStack spacing={2} alignItems='stretch' maxWidth={'100%'} wordBreak='break-word'>
                <div style={{ 
                  width: '100%', 
                  maxWidth: '100%', 
                  overflow: 'hidden',
                  display: 'block',
                  boxSizing: 'border-box'
                }}>
                  <style>
                    {`
                      /* Override Trix default styles with maximum specificity */
                      trix-toolbar {
                        width: 100% !important;
                        max-width: 100% !important;
                        min-width: 0 !important;
                        overflow: hidden !important;
                        box-sizing: border-box !important;
                      }
                      
                      trix-toolbar .trix-button-row {
                        display: flex !important;
                        flex-wrap: wrap !important;
                        overflow-x: visible !important;
                        overflow-y: visible !important;
                        width: 100% !important;
                        max-width: 100% !important;
                        min-width: 0 !important;
                        gap: 2px !important;
                        box-sizing: border-box !important;
                      }
                      
                      trix-toolbar .trix-button-group {
                        display: flex !important;
                        flex: 0 1 auto !important;
                        min-width: 0 !important;
                        margin: 2px 1px !important;
                        box-sizing: border-box !important;
                      }
                      
                      trix-toolbar .trix-button-group-spacer {
                        display: none !important;
                      }
                      
                      /* Hide file upload button and its separator */
                      trix-toolbar .trix-button--icon-attach {
                        display: none !important;
                      }
                      
                      /* Hide the separator/spacer that comes after the attach button */
                      trix-toolbar .trix-button--icon-attach + .trix-button-group-spacer {
                        display: none !important;
                      }
                      
                      /* Also hide any button group that only contains the attach button */
                      trix-toolbar .trix-button-group:has(.trix-button--icon-attach):not(:has(.trix-button:not(.trix-button--icon-attach))) {
                        display: none !important;
                      }
                      
                      /* Hide spacers that are adjacent to hidden elements */
                      trix-toolbar .trix-button-group-spacer:has(+ .trix-button--icon-attach) {
                        display: none !important;
                      }
                      
                      trix-toolbar .trix-button {
                        flex: 0 0 auto !important;
                        min-width: 28px !important;
                        max-width: 40px !important;
                        padding: 0 4px !important;
                        margin: 0 1px !important;
                        font-size: 13px !important;
                        box-sizing: border-box !important;
                      }
                      
                      trix-editor {
                        width: 100% !important;
                        max-width: 100% !important;
                        min-width: 0 !important;
                        box-sizing: border-box !important;
                      }
                      
                      /* More specific targeting */
                      .trix-editor-container trix-toolbar {
                        width: 100% !important;
                        max-width: 100% !important;
                        min-width: 0 !important;
                      }
                      
                      .trix-editor-container trix-editor {
                        width: 100% !important;
                        max-width: 100% !important;
                        min-width: 0 !important;
                      }
                    `}
                  </style>
                  <TrixEditor
                    ref={editorRef}
                    className='trix-editor-container'
                    onChange={handleChange}
                    placeholder={placeholder}
                    mergeTags={[]}
                  />
                </div>
                {!!helperText && (
                  <UiFormHelperText>
                    {helperText}
                  </UiFormHelperText>
                )}
                <UiFormErrorMessage />
              </UiStack>
            </FieldContainer>
          </UiFormControl>
        );
      }}
    </Field>
  );
};

export default RichTextAreaField;